import React, { useEffect, useState } from 'react';
import { Button, Input, Select, Typography } from 'antd';
import { ReloadOutlined, SearchOutlined, WarningOutlined } from '@ant-design/icons';
import { PracticeOption } from '../../../Types/auth';

const { Text } = Typography;

interface Props {
    onLookup: (applicationId: number, practiceId: number) => void;
    loading: boolean;
    practiceOptions: PracticeOption[];
    practiceOptionsLoading: boolean;
    practiceOptionsError?: boolean;
    onRetryPracticeOptions?: () => void;
}

// Shared label style — applied identically to both field labels so they
// always render at the same height and the inputs below them align.
const fieldLabelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: 5,
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '20px',
    color: 'var(--text-secondary)',
};

const LookupCard: React.FC<Props> = ({ onLookup, loading, practiceOptions, practiceOptionsLoading, practiceOptionsError, onRetryPracticeOptions }) => {
    const [applicationId, setApplicationId] = useState('');
    const [practiceId, setPracticeId] = useState<number | undefined>(undefined);

    // Default to practice 3 once options load, fall back to first
    useEffect(() => {
        if (practiceOptions.length > 0 && practiceId === undefined) {
            const preferred = practiceOptions.find(o => o.id === 3) ?? practiceOptions[0];
            setPracticeId(preferred.id);
        }
    }, [practiceOptions, practiceId]);

    function handleSubmit() {
        const id = parseInt(applicationId, 10);
        if (!isNaN(id) && id > 0 && practiceId !== undefined) {
            onLookup(id, practiceId);
        }
    }

    function handleKeyDown(e: React.KeyboardEvent) {
        if (e.key === 'Enter') handleSubmit();
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div
                style={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    color: 'var(--text-secondary)',
                    marginBottom: 2,
                }}
            >
                Lookup
            </div>

            <div>
                <label style={fieldLabelStyle}>Practice</label>
                {practiceOptionsError ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Text type="danger" style={{ fontSize: 12 }}>
                            <WarningOutlined /> Failed to load
                        </Text>
                        {onRetryPracticeOptions && (
                            <Button
                                size="small"
                                icon={<ReloadOutlined />}
                                onClick={onRetryPracticeOptions}
                                style={{ height: 24, fontSize: 11 }}
                            >
                                Retry
                            </Button>
                        )}
                    </div>
                ) : (
                    <Select
                        loading={practiceOptionsLoading}
                        value={practiceId}
                        onChange={setPracticeId}
                        disabled={loading || practiceOptionsLoading}
                        placeholder="Select a practice"
                        style={{ width: '100%', height: 32 }}
                        options={practiceOptions.map(o => ({
                            value: o.id,
                            label: o.city ? `${o.name} — ${o.city}, ${o.state}` : o.name,
                        }))}
                    />
                )}
            </div>

            <div>
                <label style={fieldLabelStyle}>Application ID</label>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Input
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="e.g. 1048576"
                        value={applicationId}
                        onChange={e => setApplicationId(e.target.value.replace(/\D/g, ''))}
                        onKeyDown={handleKeyDown}
                        disabled={loading}
                        style={{ height: 32, flex: 1, minWidth: 0 }}
                    />
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        loading={loading}
                        onClick={handleSubmit}
                        disabled={!applicationId || practiceId === undefined}
                        style={{ height: 32, flexShrink: 0 }}
                    >
                        Look Up
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default LookupCard;
